import { useContext, useEffect, useRef, useState } from 'react';
import { CurrentContext, supabase } from './App';
import './vasta.css';
import { __, addIndex, compose as C, chain, identity, includes, indexBy, 
    isNotEmpty, last, map as m, mapAccum, mergeAll, prop as p, reject, repeat, slice, uniqBy, 
    values, zipObj} from 'ramda';
import {  useSearchParams } from 'react-router-dom';
import { debounce, randomDelay, seatFromHashCode } from './helpers';
import { shuffler, randomLcg } from 'd3';

const fetchStep = s => supabase.rpc('get_current_step').abortSignal(s)
  .then(({ data, error }) => error ? null : data);

export function startPolling(onStep, interval = 3000, timeout = 2000) {
  const poll = async () => {
    const c = new AbortController(), t = setTimeout(() => c.abort(), timeout);
    try { const step = await fetchStep(c.signal); if (step !== null) {
        onStep(step);
    };}
    catch (e) { console.warn(e.name === 'AbortError' ? 'Timeout' : e); }
    finally { clearTimeout(t); }
  };
  setTimeout(() => { poll(); setInterval(poll, interval); }, Math.random() * interval);
}

export const Vasta = () => {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState(null);
    const [step_questions,set_step_questions] = useState({});
    const [participants, setParticipants] = useState(null);
    const stepRef = useRef({});
    const event_id = useContext(CurrentContext);
    const theEvent = useRef(null);
    const [searchParams] = useSearchParams();
    const from = searchParams.get("from")
    const to = searchParams.get("to")
    const key = from ? null : seatFromHashCode(searchParams.get("seat"));

    useEffect(() => {if(event_id) (theEvent.current = event_id);}, [event_id]);
    useEffect(() => { stepRef.current = step_questions; }, [step_questions]);

    const getSeats = async () => {
        const {data:seats} = await supabase.from("seats").select("n,area,row,start");
        const separateSeats = C(
            shuffler(randomLcg(17912965)),
            chain(x => 
                addIndex(m)((x1, n1) => 
                    ({area:x1.area,row:x1.row,key:`${x1.area}-${x1.row}-${n1+x1.start}`,seat: n1 + x1.start}), 
                    repeat(x, x.n))))
        return separateSeats(seats);  
    }

    const fetchQuestions = async() => {
        if (theEvent.current) {
            const { data:steps, error } = await supabase
                .from("steps")
                .select("*, steps_questions(*,questions(*,question_choices(*))),events!events_current_fkey(current)")
                .eq("event_id", theEvent.current)
                .order("order")
            const change_updated = await supabase.from("events").update({ updated: false }).eq("play",true);
            if (!error) {
                const formatted = C(
                    mergeAll,
                    values,
                    last,
                    mapAccum((acc, x) => [[...acc, x], { 
                        [x.id]: uniqBy(p("id"))(chain(C(m(C(p("questions"))),p("steps_questions")),[...acc, x])) 
                    }],[] )
                )(steps)
                set_step_questions(formatted);
                setQuestions(formatted[steps.find(x => isNotEmpty(x.events))?.id]);
            }
        }
    };

    useEffect(() => {
        const show_questions = ([{current,updated}]) => {
            setQuestions(stepRef.current[current]); 
            if (updated) fetchQuestions();
        };
        const stop = startPolling(show_questions, 3000, 4000);
        return stop;
    }, []);


    const checkIn = async ({seats,from,to}) => {
        const tree = C(
            indexBy(p("question_id")),
            p("responses")
        )
        const selected = from ? C(slice(from,to)) : identity

        const {data,error} = await supabase.from("participants_with_key").select("*, responses(*)")
        .in("seat_key",seats ? m(p("key"),selected(seats)) : [key])
        .eq("event_id",theEvent.current)

        const to_add = seats ? reject(x => includes(x.key,m(p("seat_key"),data)),selected(seats)) : 
            zipObj(["area","row","seat"],key.split("-"));


        const {data:data2} = to_add?.length ? await supabase
            .from("participants")
            .insert(m(({area,row,seat}) => ({area,row,seat,event_id: theEvent.current}),to_add))
            .select() : {data:[]};

        setAnswers(m(tree,indexBy(p("id"),[...data,...data2])));
        setParticipants(m(p("id"),[...data,...data2]));
    }

    const sleep = ms => new Promise(res => setTimeout(res, ms));

    const upsertResponse = async (data, existing) => {
        if (existing) {
            return supabase
            .from("responses")
            .update(data)
            .eq("id", existing.id)
            .select()
            .single();
        } else {
            return supabase
            .from("responses")
            .insert(data)
            .select()
            .single();
        }
    };

    const handleInput = (qid, val) => async e => {
        const v = val ?? e.target.value;
        setAnswers(p => participants.reduce((a,x)=>({
            ...a,[x]:{...(p?.[x]||{}),[qid]:{...(p?.[x]?.[qid]||{}),value:v}}}
        ),{...p}));
        for (const x of participants) {
            const ex = answers?.[x]?.[qid];
            const { data, error } = await upsertResponse(
                { question_id: qid, value: v, participant_id: x },
                ex
            );

            if (!error && data) {
                setAnswers(p => ({
                ...p,
                [x]: { ...(p?.[x] || {}), [qid]: { ...(p?.[x]?.[qid] || {}), ...data } }
                }));
            }

            await sleep(randomDelay());
        }
    };

    useEffect(() => {
        fetchQuestions();
        if (theEvent.current) {
            if (from && to) {
                getSeats().then(seats => {
                    checkIn({from,to,seats});
                });
            } else {
                checkIn({});
            }
        }
    }, [theEvent.current,from,to]);

    return (
        <>
            {questions?.length && !participants?.length ? <p className="loadingdots">...</p> : 
            <>
                <h2 className="seat">{from ? from+"-"+to : 
                key?.split("-").map((x,n) => n === 0 ? [,"parter","rõdu"][x] : x).join("-")
                }</h2>
                <form className="respond-form"  onSubmit={e => e.preventDefault()}>
                {m(x => (
                <label
                    className={`respond ${answers[participants[0]][x.id] ? "answered" : ""}`}
                    key={x.id}
                >
                    <p className="question-text">{x.text}</p>

                    {x.type === "number" ? (
                    <input
                        type="number"
                        value={answers[participants[0]][x.id]?.value ?? ""}
                        onInput={handleInput(x.id)}
                    />
                    ) : (
                    m(
                        y =>
                        y.type === x.type && (
                            <div key={`${x.id}-${y.id}`}>
                            <input
                                type="radio"
                                name={`question_${x.id}`}
                                value={y.value}
                                defaultChecked={
                                Number(answers[participants[0]][x.id]?.value) ===
                                Number(y.value)
                                }
                                id={`question_${x.id}_${y.id}`}
                                onInput={debounce(handleInput(x.id, y.value),1000)}
                            />
                            <label htmlFor={`question_${x.id}_${y.id}`}>{y.text}</label>
                            </div>
                        ),
                        x.question_choices
                    )
                    )}
                </label>
                ), (questions || []))}
                {questions?.length === 0 ? <div className="question-text">Selle asjani veel jõuame, praegu võid rahulikult telefoni vaikseks panna.</div> : null}
                </form>
            </>}
        </>
    );
};